import { gql, useLazyQuery } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { useEffect, useState } from "react";
import { Navigate } from "react-router";

import SpaceliftLoader from "components/loading/SpaceliftLoader";
import useMFA from "hooks/useMFA";
import { Maybe, User } from "types/generated";

/** @public (Knip ignored - used in the file and exported for testing) */
export const CHECK_ACCESS = gql`
  query CheckAccess {
    viewer {
      id
    }
  }
`;

type CheckAccessGql = {
  viewer: Maybe<User>;
};

const SSOLogin = () => {
  const [called, setCalled] = useState(false);
  const [nextUrl, setNextUrl] = useState<string | null>(null);

  const [redirectTo, , deleteRedirectTo] = useLocalStorage("redirectTo");

  const [, , deleteTemporaryToken] = useLocalStorage("temporaryToken");

  const { shouldUseMFA, shouldRegisterSecurityKey } = useMFA();

  const determineRedirect = ({ viewer }: CheckAccessGql) => {
    if (called) {
      return;
    }

    setCalled(true);

    if (viewer === null) {
      setNextUrl("/unauthorized");
      return;
    }

    const target = redirectTo || "/";
    deleteRedirectTo();
    setNextUrl(target);
  };

  const checkMFA = () => {
    if (shouldRegisterSecurityKey) {
      setNextUrl("/enable_mfa");
      return true;
    }

    if (shouldUseMFA) {
      setNextUrl("/mfa");
      return true;
    }

    return false;
  };

  const [checkAccess] = useLazyQuery<CheckAccessGql>(CHECK_ACCESS);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    deleteTemporaryToken();

    const isMFA = checkMFA();

    if (!isMFA) {
      checkAccess()
        .then(({ data }) => {
          if (data) {
            determineRedirect(data);
          } else {
            setNextUrl("/unauthorized");
          }
        })
        .catch(() => {
          setNextUrl("/unauthorized");
        });
    }
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (nextUrl) {
    return <Navigate to={nextUrl} replace />;
  }

  return <SpaceliftLoader />;
};

export default SSOLogin;
